// Library Methods
import { useId } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import {
  SynoptophoreCurrentStepType,
  SynoptophoreEyePlacementType,
} from "src/services/exams/types";

// Constants
import { eyeTypeEnums } from "src/constants/enums";

// Utils
import { formatSignedDegrees } from "../synoptophoreLiveHelper";

// Each eye's screen, in degrees: ±17° either way from straight ahead.
const HALF_FIELD = 17;
// An off-screen marker sits this far in from the edge so all of it shows.
const MARKER_INSET = 1.2;
const SURROUND_RADIUS = 10.35;

const BACKGROUND = "#08090C";
const COVER = "#777";
const MARKER = "#E7C43C";
const FONT_FAMILY = "Verdana, sans-serif";

type EyeViewType = {
  eye: string; // eyeTypeEnums.LEFT or eyeTypeEnums.RIGHT
  step: SynoptophoreCurrentStepType;
};

// Where a picture whose centre is off this eye's screen went: an arrow on the
// nearest edge, pointing the way it left.
const getOffViewMarker = (cx: number, cy: number) => {
  const overX = Math.abs(cx) > HALF_FIELD;
  const overY = Math.abs(cy) > HALF_FIELD;
  if (!overX && !overY) return null;

  const limit = HALF_FIELD - MARKER_INSET;
  const clamp = (value: number) => Math.max(-limit, Math.min(limit, value));

  let angle: number;
  if (overX && overY) angle = (Math.atan2(cy, cx) * 180) / Math.PI;
  else if (overX) angle = cx > 0 ? 0 : 180;
  else angle = cy > 0 ? 90 : -90;

  return { x: clamp(cx), y: clamp(cy), angle };
};

// One eye's half of the synoptophore as the headset draws it: the slide where
// it sits on that eye's screen, with its surround, cover and flash states.
const EyeView = ({ eye, step }: EyeViewType) => {
  const { t } = useTranslation();
  // useId's colons aren't safe inside an SVG url(#...) reference
  const gradientId = useId().replace(/:/g, "");

  const { field } = step;
  const placement: SynoptophoreEyePlacementType =
    eye === eyeTypeEnums.LEFT ? field.left : field.right;
  const image =
    eye === eyeTypeEnums.LEFT ? step.leftEyeImage : step.rightEyeImage;

  const isCovered = field.occludedEye === eye;
  const label = eye === eyeTypeEnums.LEFT ? t("Left Eye") : t("Right Eye");

  // Screen y runs down, the payload's vertical runs up.
  const cx = placement.horizontalDegrees;
  const cy = -placement.verticalDegrees;
  // Torsion is mirrored between the eyes so the same value turns both the same
  // way relative to the nose.
  const rotation =
    eye === eyeTypeEnums.LEFT
      ? -placement.torsionDegrees
      : placement.torsionDegrees;
  const rotate = `rotate(${rotation} ${cx} ${cy})`;

  const imageSize = step.imageFieldDegrees;
  const outlineSize = field.angularSizeDegrees;
  const offViewMarker = getOffViewMarker(cx, cy);

  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
        minWidth: 0,
      }}
    >
      <Box
        component="figcaption"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: "6px",
          minHeight: "20px",
        }}
      >
        <Typography fontSize="12px" fontWeight={600}>
          {label} {field.fixatingEye === eye && `- ${t("Fixating")}`}{" "}
          {field.drivenEye === eye && `- ${t("Moved by Patient")}`}
        </Typography>
      </Box>

      <svg
        viewBox={`${-HALF_FIELD} ${-HALF_FIELD} ${HALF_FIELD * 2} ${HALF_FIELD * 2}`}
        role="img"
        aria-label={label}
        style={{
          display: "block",
          width: "100%",
          aspectRatio: "1",
          borderRadius: "4px",
          overflow: "hidden",
        }}
      >
        <defs>
          <radialGradient id={gradientId}>
            <stop offset="93%" stopColor={COVER} />
            <stop offset="100%" stopColor={COVER} stopOpacity={0} />
          </radialGradient>
        </defs>
        <rect
          x={-HALF_FIELD}
          y={-HALF_FIELD}
          width={HALF_FIELD * 2}
          height={HALF_FIELD * 2}
          fill={BACKGROUND}
        />

        {isCovered ? (
          <>
            <rect
              x={-HALF_FIELD}
              y={-HALF_FIELD}
              width={HALF_FIELD * 2}
              height={HALF_FIELD * 2}
              fill={COVER}
            />
            <text
              x={0}
              y={0.6}
              textAnchor="middle"
              fill="#FFFFFFCC"
              fontSize={2}
              fontFamily={FONT_FAMILY}
              letterSpacing={0.05}
            >
              {t("Covered")}
            </text>
          </>
        ) : (
          placement.visible && (
            <>
              {field.showSurround && (
                <circle
                  cx={cx}
                  cy={cy}
                  r={SURROUND_RADIUS}
                  fill={`url(#${gradientId})`}
                />
              )}

              {/* Pictorial slides come without an image, so they're drawn as an outline */}
              {image && imageSize ? (
                <image
                  href={`data:image/png;base64,${image}`}
                  x={cx - imageSize / 2}
                  y={cy - imageSize / 2}
                  width={imageSize}
                  height={imageSize}
                  transform={rotate}
                />
              ) : (
                <rect
                  x={cx - outlineSize / 2}
                  y={cy - outlineSize / 2}
                  width={outlineSize}
                  height={outlineSize}
                  transform={rotate}
                  fill="none"
                  stroke="#FFFFFFB0"
                  strokeWidth={0.08}
                  strokeDasharray="0.35 0.25"
                />
              )}

              {offViewMarker && (
                <path
                  d="M1 0L-0.7 -0.85L-0.7 0.85Z"
                  fill={MARKER}
                  transform={`translate(${offViewMarker.x} ${offViewMarker.y}) rotate(${offViewMarker.angle})`}
                />
              )}

              {field.alternateFlashPeriodSeconds !== null && (
                <text
                  x={0}
                  y={HALF_FIELD - 1.1}
                  textAnchor="middle"
                  fill={MARKER}
                  fontSize={1.1}
                  fontFamily={FONT_FAMILY}
                >
                  {t("Shown in Turn With the Other Eye")}
                </text>
              )}
            </>
          )
        )}

        <path
          d={`M${-HALF_FIELD} 0H${HALF_FIELD}M0 ${-HALF_FIELD}V${HALF_FIELD}`}
          fill="none"
          stroke="#FFFFFF2A"
          strokeWidth={0.06}
          strokeDasharray="0.3 0.3"
        />
      </svg>

      <Typography
        fontSize="12px"
        textAlign="center"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        H {formatSignedDegrees(placement.horizontalDegrees)} · V{" "}
        {formatSignedDegrees(placement.verticalDegrees)} · T{" "}
        {formatSignedDegrees(placement.torsionDegrees)}
      </Typography>
    </Box>
  );
};

export default EyeView;
